/**
 * PreInvoiceDropdownActions - Componente de acciones para prefacturas
 *
 * Dropdown con acciones disponibles para cada prefactura:
 * - Ver: Navegar al detalle de la prefactura
 * - Facturar: Marcar la prefactura como facturada (CLOSED)
 */

import { faCircleDollar, faEye, faEllipsisVertical, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Button } from '@shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { PreInvoice } from '../../models/PreInvoices';
import { useInvoicesPermissions } from '@modules/Invoices/InvoicesOrders/List/hooks/useInvoicesPermissions';

interface PreInvoiceDropdownActionsProps {
  preInvoice: PreInvoice;
  onAction: (action: 'view' | 'invoice' | 'delete', preInvoiceId: number) => void;
}

export const PreInvoiceDropdownActions = ({ preInvoice, onAction }: PreInvoiceDropdownActionsProps) => {
  const { t } = useTranslation('preInvoicesModule');
  const { canCreateInvoices } = useInvoicesPermissions();

  const viewHref = `/invoices/pre-invoices/view/${preInvoice.id}`;

  const actions = [
    {
      id: 'view',
      label: t('actions.view', 'Ver'),
      icon: faEye,
      onClick: () => onAction('view', preInvoice.id),
      canShow: true,
      testId: 'view-preinvoice-action',
      href: viewHref,
    },
    {
      id: 'invoice',
      label: t('actions.invoice', 'Facturar'),
      icon: faCircleDollar,
      onClick: () => onAction('invoice', preInvoice.id),
      canShow: canCreateInvoices && preInvoice.status !== 'CLOSED',
      testId: 'invoice-preinvoice-action',
    },
    {
      id: 'delete',
      label: t('actions.delete', 'Eliminar'),
      icon: faTrash,
      onClick: () => onAction('delete', preInvoice.id),
      canShow: preInvoice.status !== 'CLOSED',
      testId: 'delete-preinvoice-action',
      variant: 'destructive' as const,
    },
  ].filter((action) => action.canShow);

  if (actions.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`preinvoice-actions-${preInvoice.id}`}>
          <FontAwesomeIcon icon={faEllipsisVertical} className="h-4 w-4 text-icons" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {actions.map((action) => {
          const content = (
            <>
              <FontAwesomeIcon icon={action.icon} className="mr-2 h-3.5 w-3.5 text-icons" />
              <span>{action.label}</span>
            </>
          );

          if (action.href) {
            return (
              <DropdownMenuItem key={action.id} asChild>
                <a
                  href={action.href}
                  data-testid={action.testId}
                  onClick={(event) => {
                    if (event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1) {
                      // Permitir que el navegador gestione abrir en nueva pestaña/ventana
                      return;
                    }

                    event.preventDefault();
                    action.onClick?.();
                  }}
                  onKeyDown={(event) => {
                    if ((event.key === 'Enter' || event.key === ' ') && (event.ctrlKey || event.metaKey)) {
                      event.preventDefault();
                      window.open(action.href, '_blank', 'noopener');
                    } else if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      action.onClick?.();
                    }
                  }}
                >
                  {content}
                </a>
              </DropdownMenuItem>
            );
          }

          return (
            <DropdownMenuItem
              key={action.id}
              onClick={action.onClick}
              data-testid={action.testId}
              className={action.variant === 'destructive' ? 'text-destructive focus:text-destructive' : ''}
            >
              {content}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
